import { createTheme } from '@mui/material/styles';

/**
 * Bread Bskt product UI — Teal + Slate (palette A).
 */
export const tokens = {
    ink: '#1C2430',
    inkMuted: '#5C6570',
    inkFaint: '#9AA3AD',
    canvas: '#F1F5F9',
    surface: '#FFFFFF',
    border: '#E2E8F0',
    borderStrong: '#CBD5E1',
    primary: '#0D9488',
    primaryHover: '#0F766E',
    accent: '#475569',
    accentSoft: 'rgba(71, 85, 105, 0.12)',
    primarySoft: 'rgba(13, 148, 136, 0.12)',
    honey: '#F0B429',
    honeySoft: 'rgba(240, 180, 41, 0.16)',
    danger: '#E03E2D',
    dangerSoft: '#FFF1EE',
    success: '#1FA971',
    successSoft: '#E8F8F1',
    warning: '#D4890A',
    warningSoft: '#FFF6E5',
    radius: 14,
    radiusSm: 10,
    font: '"Nunito", "Avenir Next", "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
};

const theme = createTheme({
    typography: {
        fontFamily: tokens.font,
        button: {
            textTransform: 'none',
            fontWeight: 700,
            letterSpacing: 0.01,
        },
        h1: {
            fontFamily: tokens.font,
            fontWeight: 700,
            fontSize: '1.625rem',
            letterSpacing: '-0.02em',
            color: tokens.ink,
        },
        h2: {
            fontFamily: tokens.font,
            fontWeight: 700,
            fontSize: '1.625rem',
            letterSpacing: '-0.02em',
            color: tokens.ink,
        },
        h5: {
            fontFamily: tokens.font,
            fontWeight: 700,
            fontSize: '1.625rem',
            letterSpacing: '-0.02em',
            color: tokens.ink,
        },
    },
    shape: {
        borderRadius: tokens.radiusSm,
    },
    palette: {
        mode: 'light',
        primary: {
            main: tokens.primary,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: tokens.accent,
        },
        error: {
            main: tokens.danger,
        },
        success: {
            main: tokens.success,
        },
        warning: {
            main: tokens.warning,
        },
        text: {
            primary: tokens.ink,
            secondary: tokens.inkMuted,
        },
        divider: tokens.border,
        background: {
            default: tokens.canvas,
            paper: tokens.surface,
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    fontWeight: 700,
                    paddingInline: 18,
                    minHeight: 40,
                },
                contained: {
                    backgroundColor: tokens.primary,
                    color: '#fff',
                    '&:hover': {
                        backgroundColor: tokens.primaryHover,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#E8EDF3',
                        color: '#A0AAB6',
                    },
                },
                outlined: {
                    borderColor: tokens.borderStrong,
                    color: tokens.ink,
                    backgroundColor: tokens.surface,
                    '&:hover': {
                        borderColor: tokens.primary,
                        backgroundColor: tokens.primarySoft,
                        color: tokens.primaryHover,
                    },
                },
                text: {
                    color: tokens.ink,
                    '&:hover': {
                        backgroundColor: tokens.accentSoft,
                        color: tokens.accent,
                    },
                },
                sizeSmall: {
                    minHeight: 34,
                    paddingInline: 14,
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: tokens.radiusSm,
                    backgroundColor: tokens.surface,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.borderStrong,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.accent,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.primary,
                        borderWidth: 1.5,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 20,
                    border: `1px solid ${tokens.border}`,
                    boxShadow: '0 12px 40px rgba(28, 36, 48, 0.12)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontWeight: 800,
                    fontSize: '1.125rem',
                    color: tokens.ink,
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 999,
                    borderColor: tokens.borderStrong,
                    color: tokens.inkMuted,
                    '&.Mui-selected': {
                        backgroundColor: tokens.primary,
                        color: '#fff',
                        borderColor: tokens.primary,
                        '&:hover': {
                            backgroundColor: tokens.primaryHover,
                        },
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    fontWeight: 700,
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: tokens.font,
                    backgroundColor: tokens.canvas,
                },
                'h1, h2, h3, h4, h5, h6': {
                    fontFamily: tokens.font,
                },
            },
        },
    },
});

export default theme;
